import "./lib/sentry.js";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext.jsx";
import { ClientCoachAccessProvider } from "./context/ClientCoachAccessContext.jsx";
import { CoachBillingProvider } from "./context/CoachBillingContext.jsx";
import { useRole } from "./hooks/useRole.js";
import AuthLoadingScreen from "./components/AuthLoadingScreen.jsx";
import "@appwrite.io/pink-icons";
import "./index.css";

import HomePage from "./pages/HomePage.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import DashboardPage from "./pages/DashboardPage.jsx";
import BillingPage from "./pages/BillingPage.jsx";
import PlanPage from "./pages/PlanPage.jsx";
import SettingsPage from "./pages/SettingsPage.jsx";
import ResetPasswordPage from "./pages/ResetPasswordPage.jsx";
import RegisterPage from "./pages/RegisterPage.jsx";
import ProgramPage from "./pages/ProgramPage.jsx";
import ProgramsPage from "./pages/ProgramsPage.jsx";
import ClientProgramsPage from "./pages/ClientProgramsPage.jsx";
import ClientBillingPage from "./pages/ClientBillingPage.jsx";
import CoachClientPlansPage from "./pages/CoachClientPlansPage.jsx";
import SuccessPage from "./pages/SuccessPage.jsx";
import PrivacyPage from "./pages/PrivacyPage.jsx";
import SupportPage from "./pages/SupportPage.jsx";
import RoleRoute from "./components/RoleRoute.jsx";
import ClientAccessGate from "./components/ClientAccessGate.jsx";
import { Sentry } from "./lib/sentry.js";

const coachOnly = (element) => <RoleRoute allowedRole="coach">{element}</RoleRoute>;
const anyAuth = (element) => <RoleRoute allowedRole="any">{element}</RoleRoute>;

function ProgramsRoute() {
  const { status } = useAuth();
  const role = useRole();
  if (status === "loading" || role === "loading") {
    return <AuthLoadingScreen />;
  }
  if (role === "unauthenticated") return <Navigate to="/login" replace />;
  if (role === "coach") return <ProgramsPage />;
  return (
    <ClientAccessGate>
      <ClientProgramsPage />
    </ClientAccessGate>
  );
}

const router = createBrowserRouter([
  { path: "/", element: <HomePage /> },
  { path: "/privacy", element: <PrivacyPage /> },
  { path: "/support", element: <SupportPage /> },
  { path: "/login", element: <LoginPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  { path: "/register", element: <RegisterPage /> },
  { path: "/programs/:id", element: <ProgramPage /> },
  { path: "/dashboard", element: coachOnly(<DashboardPage />) },
  { path: "/programs", element: <ProgramsRoute /> },
  { path: "/billing", element: coachOnly(<BillingPage />) },
  { path: "/client-plans", element: coachOnly(<CoachClientPlansPage />) },
  { path: "/plan", element: anyAuth(<PlanPage />) },
  { path: "/settings", element: anyAuth(<SettingsPage />) },
  { path: "/billing-settings", element: anyAuth(<ClientBillingPage />) },
  { path: "/success", element: anyAuth(<SuccessPage />) },
]);

function AppRouter() {
  return <RouterProvider router={router} />;
}

function SentryFallback() {
  return (
    <div className="hc-page-shell flex min-h-screen items-center justify-center p-6">
      <p className="text-center text-hc-muted">Something went wrong. Please refresh the page.</p>
    </div>
  );
}

async function bootstrap() {
  if (import.meta.env.VITE_E2E_MOCK === "1") {
    await import("../e2e/mocks/install.js");
  }

  createRoot(document.getElementById("root")).render(
    <Sentry.ErrorBoundary fallback={<SentryFallback />}>
      <StrictMode>
        <AuthProvider>
          <CoachBillingProvider>
            <ClientCoachAccessProvider>
              <AppRouter />
            </ClientCoachAccessProvider>
          </CoachBillingProvider>
        </AuthProvider>
      </StrictMode>
    </Sentry.ErrorBoundary>,
  );
}

bootstrap();
